import { CommonModule } from "@angular/common";
import {
  Component,
  Input,
  OnInit,
  TemplateRef,
  ViewChild,
  AfterViewInit
} from "@angular/core";
import { MatTableModule } from "@angular/material/table";
import { MatPaginator } from "@angular/material/paginator";
import { MatIcon } from "@angular/material/icon";
import { MatDialog } from "@angular/material/dialog";
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatButtonModule } from "@angular/material/button";
import { Observable, merge, of as observableOf, Subject } from "rxjs";
import { catchError, map, startWith, switchMap, filter, delay } from "rxjs/operators";
import { MatSort, MatSortModule } from "@angular/material/sort";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatTabsModule } from "@angular/material/tabs";
import { SocialAIContentSIteI } from "../../../interfaces/core.interface";
import { CoreService } from "../../../services/core.service";
import { DialogComponent } from "../../dialog/dialog.component";
import { SocialAiSiteFormComponent } from "../../forms/create-account/site-form/site-form.component";
import { MatTooltipModule } from "@angular/material/tooltip";
import { ActivatedRoute, Router } from "@angular/router";

type Post = {
  id: number;
  rss_title: string;
  domain_name?: string;
  image_url: string;
  post_status?: string;
  enable_repeat?: number;
  parent_post_id?: number;
  tag?: string;
  social_platform_title?: string;
  scheduled_for?: string | Date;
  rss_description?: string;
};

type Alert = {
  id: number;
  contentsite_id: number;
  alert_text: string;
  alert_type: string;
  active: number;
  platform: string;
  format: string;
  rss_img_url: string;
  rss_title: string;
  created_at: string | Date;
  Domain: string;
  posting_queue_id: number;
};

@Component({
  selector: "app-social-ai-site-table",
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatSortModule,
    MatPaginator,
    MatIcon,
    MatToolbarModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatInputModule,
    MatSelectModule,
    FormsModule,
    MatTabsModule,
    SocialAiSiteFormComponent,
    DialogComponent,
    MatTooltipModule
  ],
  templateUrl: "./social-ai-site-table.component.html",
  styleUrls: ["./social-ai-site-table.component.scss"]
})
export class SocialAiSiteTableComponent implements AfterViewInit, OnInit {
  private _companyID: string | null = null;
  private companyIdStream$ = new Subject<void>();

  maxSidebarCards = 3;
  posts: Post[] = [];
  alerts: Alert[] = [];
  naPlaceholder = "./assets/images/na.png";
  private readonly LATEST_POSTS_KEY = "latestPosts";
  private readonly LATEST_ALERTS_KEY = "latestAlerts";

  fullLogoPreview: string | null = null;
  thumbPreview: string | null = null;

  @Input()
  set companyID(id: string | null) {
    if (this._companyID !== id) {
      this._companyID = id;
      if (this.topPaginator) this.topPaginator.pageIndex = 0;
      if (this.bottomPaginator) this.bottomPaginator.pageIndex = 0;
      this.companyIdStream$.next();
    }
  }

  get companyID(): string | null {
    return this._companyID;
  }

  @ViewChild("topPaginator") topPaginator!: MatPaginator;
  @ViewChild("bottomPaginator") bottomPaginator!: MatPaginator;

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild("siteFormTmpl", { static: true }) siteFormTmpl!: TemplateRef<any>;
  @ViewChild("confirmDeleteSiteTmpl", { static: true }) confirmDeleteSiteTmpl!: TemplateRef<any>;

  dataSource: SocialAIContentSIteI[] = [];
  totalItems: number = 0;
  displayedColumns: string[] = ["ID", "Domain", "siteUrl", "status"];
  selectedSite: SocialAIContentSIteI = {} as SocialAIContentSIteI;
  private AllowNewDomain: boolean = true;

  constructor(
    private coreService: CoreService,
    public dialog: MatDialog,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadSidebarPosts();
    this.loadAlertsFromStorage();
  }
  private get activePaginator(): MatPaginator {
    return this.bottomPaginator ?? this.topPaginator;
  }

  ngAfterViewInit(): void {
    this.sort.sortChange.subscribe(() => {
      this.syncPaginators(0, this.activePaginator?.pageSize ?? 12);
      this.companyIdStream$.next();
    });

    merge(
      this.sort.sortChange,
      this.topPaginator.page,
      this.bottomPaginator.page,
      this.companyIdStream$
    )
      .pipe(
        startWith({}),
        delay(0),
        filter(() => !!this._companyID),
        switchMap(() => this.getSitesDataObservable()),
        catchError(() => observableOf({ data: [], total: 0 }))
      )
      .subscribe((res) => {
        this.dataSource = res.data;
        this.totalItems = res.total;
      });
  }

  onPage(which: "top" | "bottom"): void {
    const src = which === "top" ? this.topPaginator : this.bottomPaginator;
    this.syncPaginators(src.pageIndex, src.pageSize);
    this.companyIdStream$.next();
  }

  private syncPaginators(pageIndex: number, pageSize: number): void {
    if (this.topPaginator) {
      this.topPaginator.pageIndex = pageIndex;
      this.topPaginator.pageSize = pageSize;
    }
    if (this.bottomPaginator) {
      this.bottomPaginator.pageIndex = pageIndex;
      this.bottomPaginator.pageSize = pageSize;
    }
  }

  private getSitesDataObservable(): Observable<{ data: SocialAIContentSIteI[]; total: number }> {
    const p = this.activePaginator;

    const params: any = {
      sortBy: this.sort?.active ?? "ID",
      sortOrder: this.sort?.direction ?? "asc",
      offset: (p?.pageIndex ?? 0) * (p?.pageSize ?? 12),
      limit: p?.pageSize ?? 12
    };

    if (this.companyID) {
      params.companyID = Number(this.companyID);
    } else {
      console.warn("getSitesDataObservable called without companyID");
      return observableOf({ data: [], total: 0 });
    }

    return this.coreService.getData("/getSites", params).pipe(
      map((response: { data: SocialAIContentSIteI[]; total: number }) => ({
        data: response.data ?? [],
        total: response.total ?? 0
      })),
      catchError((error) => {
        console.error("Error fetching sites:", error);
        return observableOf({ data: [], total: 0 });
      })
    );
  }

  loadSites(): void {
    if (!this._companyID) {
      this.coreService.showSnackbar("Please select a company before loading sites.");
      return;
    }
    this.syncPaginators(0, this.activePaginator?.pageSize ?? 12);
    this.companyIdStream$.next();
  }

  addNewSite(): void {
    if (!this.companyID) {
      this.coreService.showSnackbar("Please select a company first.");
      return;
    }

    if (this.AllowNewDomain) {
      this.selectedSite = {
        companyID: Number(this.companyID)
      } as SocialAIContentSIteI;

      this.fullLogoPreview = null;
      this.thumbPreview = null;

      this.dialog.open(DialogComponent, {
        data: {
          content: this.siteFormTmpl,
          header: "Add Content Site",
          cls: "!bg-slate-600 !text-white"
        },
        minWidth: "80vw",
        maxWidth: "700px"
      });
    } else {
      this.coreService.showSnackbar(
        "You have exceed max number of domains allowed by your economic plan."
      );
    }
  }

  editSite(row: SocialAIContentSIteI): void {
    if (!this.companyID) {
      this.coreService.showSnackbar("Company ID is not set.");
      return;
    }

    this.selectedSite = {
      ...row,
      id: row.ID || (row as any).id
    } as SocialAIContentSIteI;

    this.fullLogoPreview = row.logo_file_path ?? null;
    this.thumbPreview = row.thumbnail_file_path ?? null;

    this.dialog.open(DialogComponent, {
      data: {
        content: this.siteFormTmpl,
        header: "Edit Site",
        cls: "!bg-slate-600 !text-white"
      },
      minWidth: "80vw",
      maxWidth: "700px"
    });
  }

  onSubmitClbk(): void {
    this.dialog.closeAll();
    this.loadSites();
  }

  // Sidebar helpers
  private loadSidebarPosts(): void {
    if (typeof window === "undefined") {
      this.posts = [];
      return;
    }

    const raw = localStorage.getItem(this.LATEST_POSTS_KEY);
    if (!raw) {
      this.posts = [];
      return;
    }

    try {
      const parsed = JSON.parse(raw) as Post[] | null;
      this.posts = Array.isArray(parsed) ? parsed.slice(0, this.maxSidebarCards) : [];
    } catch (e) {
      console.error("Failed to parse latest posts from localStorage", e);
      this.posts = [];
    }
  }

  private loadAlertsFromStorage(): void {
    if (typeof window === "undefined") return;

    const raw = localStorage.getItem(this.LATEST_ALERTS_KEY);
    if (!raw) return;

    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        this.alerts = parsed.slice(0, this.maxSidebarCards);
      }
    } catch (e) {
      console.error("Failed to parse alerts from storage", e);
    }
  }

  viewAlert(alert: Alert): void {
    const targetId = alert.posting_queue_id;
    this.router.navigate(["/preview-post", alert.format, targetId]);
  }

  viewRow(row: Post) {
    const targetId = row.enable_repeat === -1 ? row.parent_post_id ?? row.id : row.id;
    this.router.navigate(["/preview-post", row.tag, targetId]);
  }

  trackById = (_: number, item: Post) => item.id;
  trackByAlertId = (_: number, item: Alert) => item.id;

  onMainImgError(ev: Event): void {
    const img = ev.target as HTMLImageElement;
    if (!img || img.src.endsWith(this.naPlaceholder)) return;
    img.src = this.naPlaceholder;
  }

  platformTextClass(plat?: string) {
    const t = (plat || "").toLowerCase().trim();
    switch (t) {
      case "instagram":
        return "text-fuchsia-600";
      case "facebook":
        return "text-blue-600";
      case "x":
        return "text-neutral-700";
      case "youtube":
        return "text-red-600";
      default:
        return "text-gray-700";
    }
  }
}
